'use client';

import { useMemo, useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/styled/text';
import { ChatSuggestionCategoryPills } from './ChatSuggestionCategoryPills';
import { ChatSuggestionCategoryPanel } from './ChatSuggestionCategoryPanel';
import type { ChatCopilotEmptyStateLayoutProps } from './types';

export function ChatCopilotEmptyStateLayout({
  variant,
  header,
  leading,
  welcomeMessage,
  title,
  categories,
  closeCategoryLabel,
  onPromptClick,
  pillVariant,
  pillRowIndexes,
  showCategoryIconInPanel = false,
}: ChatCopilotEmptyStateLayoutProps) {
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const activeCategory = useMemo(
    () =>
      activeCategoryId === null
        ? undefined
        : categories.find((category) => category.id === activeCategoryId),
    [activeCategoryId, categories],
  );

  const resolvedPillVariant =
    pillVariant ?? (variant === 'hero' ? 'text-pill' : 'icon-pill');

  if (variant === 'hero') {
    return (
      <VStack
        alignItems="center"
        gap={2.5}
        w="full"
        px={4}
        py={4}
        textAlign="center"
      >
        {header}

        <VStack alignItems="center" gap={1} w="full">
          {title ? (
            <Text fontSize="md" color="fg.default" lineHeight="relaxed">
              {title}
            </Text>
          ) : null}
          <Text fontSize="sm" color="fg.muted" lineHeight="snug">
            {welcomeMessage}
          </Text>
        </VStack>

        <ChatSuggestionCategoryPills
          categories={categories}
          activeCategoryId={activeCategoryId}
          onSelectCategory={setActiveCategoryId}
          variant={resolvedPillVariant}
          pillRowIndexes={pillRowIndexes}
        />

        {activeCategory ? (
          <ChatSuggestionCategoryPanel
            category={activeCategory}
            closeLabel={closeCategoryLabel}
            onClose={() => setActiveCategoryId(null)}
            onPromptClick={onPromptClick}
            showCategoryIcon={showCategoryIconInPanel}
            borderRadius="l2"
          />
        ) : null}
      </VStack>
    );
  }

  return (
    <VStack alignItems="stretch" gap={4} w="full" px={2} py={1} pb={2}>
      {leading}

      <Text fontSize="sm" color="fg.default" lineHeight="snug">
        {welcomeMessage}
      </Text>

      <ChatSuggestionCategoryPills
        categories={categories}
        activeCategoryId={activeCategoryId}
        onSelectCategory={setActiveCategoryId}
        variant={resolvedPillVariant}
        pillRowIndexes={pillRowIndexes}
      />

      {activeCategory ? (
        <ChatSuggestionCategoryPanel
          category={activeCategory}
          closeLabel={closeCategoryLabel}
          onClose={() => setActiveCategoryId(null)}
          onPromptClick={onPromptClick}
          showCategoryIcon={showCategoryIconInPanel}
          panelId={
            activeCategory ? `copilot-category-${activeCategory.id}` : undefined
          }
        />
      ) : null}
    </VStack>
  );
}
